import { LitElement, css, html, nothing } from '@lit'
import styles from '@nova/styles'
import { formatPrice, updateCartQuantity, t } from '@nova/utils'

class NovaCartItem extends LitElement {
  static properties = {
    item: {
      type: Object,
    },
    compareAtPrice: {
      type: String,
    },
    inventoryPolicy: {
      type: Boolean,
    },
    max: {
      type: String,
    },
  }

  constructor() {
    super()
    this.item = {}
    this.compareAtPrice = 0
    this.inventoryPolicy = false
    this.max = ""
  }

  get formattedFinalPrice() {
    if (!this.item.final_price) return nothing
    return formatPrice(this.item.final_price * this.item.quantity)
  }

  get formattedLinePrice() {
    if (!this.item.line_price) return nothing
    return formatPrice(this.item.line_price)
  }

  get formattedOriginalLinePrice() {
    if (!this.item.original_line_price) return nothing
    return formatPrice(this.item.original_line_price)
  }

  get formattedCompareAtPrice() {
    if (this.compareAtPrice === 0) return nothing
    return formatPrice(this.compareAtPrice * this.item.quantity)
  }

  get formattedOriginalPrice() {
    if (!this.item.original_price) return nothing
    return formatPrice(this.item.original_price * this.item.quantity)
  }

  get displayTitle() {
    const title = this.item?.product_title ?? '';
    const [firstPart] = title.split('|');
    return firstPart.trim();
  }

  get _removeItemLabel() {
    const template = t('cart.general.remove_title', t('accessibility.remove_item', 'Remove item'))
    if (!this.displayTitle || !template.includes('{{ title }}')) return template
    return template.replace('{{ title }}', this.displayTitle)
  }

  _renderPrice() {
    const hasCompareAtPrice = () => {
      const comparePrice =
        Number(this.item.compare_at_price) || Number(this.compareAtPrice)

      return comparePrice > Number(this.item.final_price)
    }

    const hasDiscount = () => {
      const discounts = this.item.discount_allocations || this.item.discounts || []
      return (
        discounts.length > 0 &&
        Number(this.item.line_price) < Number(this.item.original_line_price)
      )
    }

    if (this.item.final_price === 0) {
      return html` <span>${this.formattedFinalPrice}</span> `
    } else if (hasDiscount()) {
      return html`
        <dl class="cart-item__discounted-prices">
          <dt class="visually-hidden">Regular Price</dt>
          <dd>
            <s class="cart-item__compare-at-price">
              ${this.formattedOriginalLinePrice}
            </s>
          </dd>
          <dt class="visually-hidden">Discount Price</dt>
          <dd>${this.formattedLinePrice}</dd>
        </dl>
      `
    } else if (hasCompareAtPrice()) {
      return html`
        <dl class="cart-item__discounted-prices">
          <dt class="visually-hidden">Regular Price</dt>
          <dd>
            <s class="cart-item__compare-at-price">
              ${this.formattedCompareAtPrice}
            </s>
          </dd>
          <dt class="visually-hidden">Sale Price</dt>
          <dd>${this.formattedFinalPrice}</dd>
        </dl>
      `
    } else {
      return html`<span>${this.formattedFinalPrice}</span>`
    }
  }

  _renderDiscountAllocations() {
    const discount_allocations = this.item.discount_allocations || this.item.discounts || []
    return html`
      <div class="cart-item__discount-allocations">
        ${discount_allocations.map(
          (allocation) => {
              const formattedDiscountAmount = formatPrice(allocation.amount)
              return html`
                <div class="cart-item__discount-allocation">
                  <span class="cart-item__discount-title">${t('cart.general.discount_applied')}:</span>
                  <span class="cart-item__discount-amount">${allocation.title}</span>
                </div>
              `
          }
        )}
      </div>
    `
  }

  _renderOptions() {
    const optionString = this.item.options_with_values
      .map(option => option.value)
      .join('/')

    return html` <div class='cart-item__options flex'>
        <dt></dt>
        <dd class='ml025 capitalize'>${optionString}</dd>
      </div>`
  }

  _renderInventoryCallout() {
    let message = ''
    const inventoryQty = Number(this.item.properties._inventory_qty) || 1
    if (inventoryQty < __NOVA_GLOBAL__.onlyXLeftThreshold) {
      message = String(t('general.inventory_call_out.only_x_left'))
        .replace('{{ count }}', inventoryQty)
    } else if (inventoryQty < __NOVA_GLOBAL__.onlyFewLeftThreshold) {
      message = t('general.inventory_call_out.only_few_left')
    }

    if (!message) return nothing

    return html`
      <div class="product__callout">
        <p class="product__callout-text">${message}</p>
      </div>
    `
  }


  _renderPreOrderBadge() {
    const label = this.item.pre_order_label || this.item.properties?._pre_order
    if (!label) return nothing

    return html`
      <div class="cart-item__badge">
        <span class="cart-item__badge-label">
          ${label}
        </span>
      </div>
    `
  }

  _renderAttributes() {
    if (!this.item.properties || !Object.keys(this.item.properties).length) return nothing
    const internalPropertyKeys = ['_shq_cart_id', '_call_out_color', '_call_out', '_inventory_qty', '_pre_order']
    const isInternalProperty = (key, value) =>
      internalPropertyKeys.some(
        (k) => key.includes(k) || value?.toString()?.includes(k)
      );

    return Object.entries(this.item.properties)
      .filter(([key, value]) => !isInternalProperty(key, value))
      .map(
        ([key, value]) => html`<div class="cart-item__attributes flex">
          <span>${value}</span>
        </div>`,
      )
  }

  _handleQuantityChange = (event) => {
    this.updateQuantity(event.detail.value)
    this.dispatchEvent(new CustomEvent('nova-change'))
    this.focusUpdatedInput(event.detail)
  }

  async updateQuantity(quantity) {
    this.loading = true
    await updateCartQuantity(
      [
        {
          id: this.item.key,
          quantity,
        },
      ],
      false,
    )
    this.loading = false
    document.dispatchEvent(new CustomEvent('nova:changeQuantityFinished'), {
      bubbles: true,
    })
  }

  focusUpdatedInput(detail) {
    if (detail.key === this.item.key) {
      const input = this.shadowRoot
        .querySelector("nova-quantity-input")
        ?.shadowRoot.querySelector("input")
      input.focus()
    }
  }

  render() {
    const imageUrl = this.item.featured_image?.url || this.item.image
    const imageAlt =
      this.item.featured_image?.alt || this.item.product_title || ''

    return html`
      <div class="cart-item__media">
        <a href=${this.item.url}>
          ${
            imageUrl
              ? html`<img
                class="cart-item__image cover"
                src=${imageUrl}
                alt=${imageAlt}
                width="102"
              />`
              : nothing
          }
        </a>
        ${this._renderPreOrderBadge()}
      </div>
      <div class="cart-item__details">
        <div class="cart-item-details__top">
          <a href=${this.item.url}>${this.displayTitle}</a>
          ${this.item.options_with_values ? this._renderOptions() : nothing}
          <div class="cart-item__additionals">
            ${this._renderAttributes()}
            ${this._renderDiscountAllocations()}
          </div>
        </div>
      </div>
      <span class="cart-item__prices">
        <div class="cart-item__price-wrapper">
          ${this._renderPrice()}
        </div>
      </span>
      <nova-quantity-input
        initial=${this.item.quantity}
        @changeQuantityStarted=${this._handleQuantityChange}
        class="cart-item__quantity-input"
        max="${!this.inventoryPolicy ? this.max : undefined}"
      ></nova-quantity-input>
      <sl-button
        variant="text"
        class="cart-item__remove"
        @click=${() => this.updateQuantity(0)}
        aria-label=${this._removeItemLabel}
      >
        ${t('cart.general.remove', t('accessibility.remove_item', 'Remove item'))}
      </sl-button>
    `
  }

  static styles = [
    styles.flex,
    css`
      :host {
        width: 100%;
        padding: 16px 0;
        border-top: 1px solid var(--nova-brand-grey-100);
        display: grid;
        grid-template-columns: 92px 1fr 25%;
        grid-template-areas:
          'media details price'
          'media quantity remove';

        @media screen and (min-width: 750px) {
          grid-template-columns: 118px 1fr 25%;
        }
      }
      .cart-item__media {
        grid-area: media;
        display: flex;
        padding-right: 8px;
        position: relative;

        @media screen and (min-width: 768px) {
          padding-right: 16px;
        }
      }
      .cart-item__media a {
        height: 100%;
        display: flex;
        align-items: center;
      }
      .cart-item__media img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      .cart-item__details {
        grid-area: details;
        font-size: 12px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        & * {
          line-height: 135%;
        }

        & p {
          font-size: 12px;
          line-height: 135%;
        }
      }
      .cart-item__details a {
        color: var(--nova-color-text);
        text-decoration: none;
        text-transform: capitalize;
      }
      .cart-item__options {
        text-transform: capitalize;
        margin: var(--nova-space-3xs) 0;

        & dt:empty + dd {
          margin-left: 0;
        }
      }
      .cart-item__options dd {
        margin-left: 5px;
      }
      .product__callout {
        margin: var(--nova-space-3xs) 0 0;
      }
      .product__callout-text {
        margin: 0;
        color: var(--nova-color-danger);
        font: var(--nova-body-font);
      }
      .cart-item__attributes {
        margin: var(--nova-space-3xs) 0;
        color: var(--nova-color-danger);
      }
      .cart-item__prices {
        grid-area: price;
        text-align: end;
        font-size: 14px;

        @media screen and (min-width: 768px) {
          padding-right: 18px;
        }
      }
      .cart-item__price-wrapper .visually-hidden {
        visibility: hidden;
        display: none;
      }
      .cart-item__discounted-prices {
        margin: 0;
        display: flex;
        gap: 4px;
        justify-content: flex-end;

        & dd {
          color: var(--nova-color-danger);
          margin-inline-start: 0;
        }
      }
      .cart-item__compare-at-price {
        color: var(--nova-brand-grey-200);
      }
      .cart-item__remove {
        grid-area: remove;
        display: flex;
        justify-content: end;
        align-items: end;
        align-self: flex-end;
        height: var(--nova-button-height-s);

        @media screen and (min-width: 768px) {
          padding-right: 18px;
        }
      }
      .cart-item__remove::part(base) {
        color: var(--nova-brand-grey-200);
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 135%;
        letter-spacing: normal;
        width: min-content;
        text-decoration-line: underline;
        text-decoration-style: solid;
        text-decoration-skip-ink: none;
        text-decoration-thickness: auto;
        text-underline-offset: auto;
        text-underline-position: from-font;
        display: flex;
        align-items: end;
      }
      .cart-item__remove::part(label) {
        padding: 0;
      }
      .cart-item__quantity-input {
        grid-area: quantity;
        display: flex;
        justify-content: end;
        align-items: end;
        align-self: flex-end;
        width: 122px;
        height: var(--nova-button-height-s);
      }
      .cart-item__quantity-input::part(wrapper) {
        border: 1px solid var(--nova-brand-grey-100);
        border-radius: 0;
        height: var(--nova-forms-input-height-xs);
      }
      .cart-item__quantity-input::part(decrease),
      .cart-item__quantity-input::part(increase) {
        height: var(--nova-button-height-xs);
        width: calc(var(--nova-space-sm) + 8px + var(--nova-space-sm));
        padding: 0 var(--nova-space-sm);

        & svg {
          width: 8px;
        }
      }
      @media screen and (min-width: 768px) {
        .cart-item__quantity-input::part(decrease),
        .cart-item__quantity-input::part(increase) {
          width: calc(var(--nova-space-xs-1) + 8px + var(--nova-space-xs-1));
          padding: 0 var(--nova-space-xs-1);
        }
      }
      .cart-item__quantity-input::part(decrease) {
        border-right: 1px solid var(--nova-brand-grey-100);
      }
      .cart-item__quantity-input::part(increase) {
        border-left: 1px solid var(--nova-brand-grey-100);
      }

      .cart-item__discount-title,
      .cart-item__discount-amount {
        font-size: 12px;
        line-height: 135%;
        color: var(--nova-color-danger);
      }

      .cart-item__additionals {
        & div:nth-child(1n + 3) {
          display: none;
        }
      }

      .cart-item__discount-allocation {
        margin: var(--nova-space-3xs) 0;
      }

      .cart-item__badge {
        position: absolute;
        top: 5px;
        left: 5px;
      }

      .cart-item__badge-label {
        display: inline-block;
        padding: 0 5px;
        font-size: 12px;
        line-height: 135%;
        color: var(--pre-order-text-color, #000);
        background-color: var(--pre-order-bg-color, #fff);
      }
    `,
  ]
}

customElements.define('nova-cart-item', NovaCartItem)
